import type { BaseAPIResponse } from '~/types/api';
import type { Contact, ContactCreatePayload, ContactUpdatePayload, MinorEligibility } from '~/types/person/contacts';
import { pbRequest, pbRequestRaw, pbRequestVoid } from '~/services/request';

interface ContactsResponse {
    data: Contact[];
    self?: Contact;
}

export const contactsService = {
    async getContacts(personId: number, includeSelf: boolean = false): Promise<ContactsResponse> {
        const nuxtApp = useNuxtApp();

        // Base URL already has query params (?per_page=10000&blocked=false), so use & to append
        const url = includeSelf
            ? `${nuxtApp.$api.routes.people.contacts.get_contacts(personId)}&include_self=true`
            : nuxtApp.$api.routes.people.contacts.get_contacts(personId);

        // The envelope is read whole: `self` sits beside `data`, outside it.
        const envelope = await pbRequestRaw<BaseAPIResponse<Contact[]> & { self?: Contact }>(url, {
            method: 'GET',
            failure: 'Failed to fetch contacts',
        });

        return {
            data: envelope.data,
            self: envelope.self,
        };
    },

    async createContact(personId: number, contactData: ContactCreatePayload): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.contacts.create_contact(personId), {
            method: 'POST',
            body: [contactData],
            expect: 201,
            failure: 'Failed to create contact',
        });
    },

    /**
     * Replace a contact with the payload given. The endpoint answers with the contact as it
     * now stands, which is what callers thread forward: re-reading the list to learn what this
     * call already returned is a second image of one record that a concurrent edit can change.
     */
    async updateContact(personId: number, contactId: number, payload: ContactUpdatePayload): Promise<Contact> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<Contact>(nuxtApp.$api.routes.people.contacts.update_contact(personId, contactId), {
            method: 'PUT',
            body: payload,
            failure: 'Failed to update contact',
        });
    },

    async checkMinorEligibility(personId: number): Promise<MinorEligibility> {
        const nuxtApp = useNuxtApp();

        // The endpoint answers with the eligibility itself, not a `data` envelope.
        return await pbRequestRaw<MinorEligibility>(nuxtApp.$api.routes.people.contacts.minor_eligibility(personId), {
            method: 'GET',
            failure: 'Failed to check minor eligibility',
        });
    },

    async signMinorConsent(personId: number, signerLegalName: string): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.contacts.sign_minor_consent(personId), {
            method: 'POST',
            body: { signer_legal_name: signerLegalName },
            expect: [200, 204],
            failure: 'Failed to sign minor consent',
        });
    },

    async createMinorChild(personId: number, childData: {
        first_name: string;
        last_name: string;
        dob: string;
    }): Promise<Contact> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<Contact>(nuxtApp.$api.routes.people.contacts.create_minor(personId), {
            method: 'POST',
            body: childData,
            expect: [200, 201],
            failure: 'Failed to create minor child',
        });
    },

    async deleteContact(personId: number, contactId: number): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.contacts.delete(personId, contactId), {
            method: 'DELETE',
            expect: 204,
            failure: 'Failed to delete contact',
        });
    },
};
